/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React, { useEffect, useState } from "react";

import algoliasearch from "algoliasearch";
import { InstantSearch } from "react-instantsearch-dom";
import { connectHits, connectSearchBox, connectStateResults } from "react-instantsearch-dom";
import styled from "styled-components";
import { useOnClickOutside } from "usehooks-ts";

import { Layer } from "@vienna/common";

import { useCurrentUser } from "../../../app/useCurrentUser";
import { ALGOLIA_INDEX_NAME } from "../../../lib/config";
import { SearchHit } from "../../../lib/types";
import { API } from "src/classes/api";
import { Plate } from "src/classes/plate/Plate";
import { BaseUser } from "src/classes/user";
import { SharablePageLink } from "src/components/Plate/ShareMenu/SharablePageLink";
import { Lock } from "src/components/Plate/Topbar/lib/Buttons";

import { ShareInput } from "./lib/styles";
import { SharedUser } from "./SharedUser";

const SHARE_MENU_WIDTH = 330;

interface Props {
  close: () => void;
  plate: Plate;
}

export const ShareMenu = React.forwardRef<HTMLDivElement, Props>(({ close, plate }, ref) => {
  const currentUser = useCurrentUser();
  const currentUserIsOwner = plate.observe((state) => state.currentUserIsOwner);
  plate.observe((state) => state.editorIds);
  const editors = plate.editors;
  const isPublic = plate.observe((state) => state.isPublic);
  const searchClient = algoliasearch("T4XM1HXWPD", currentUser?.snapshot.algoliaApiKey || "");
  const [allUsersLol, setAllUsersLol] = useState<BaseUser[]>([]);

  useEffect(() => {
    if (!currentUserIsOwner) return;
    // @todo this needs to be algolia...
    API.users.getAll().then((users) => {
      setAllUsersLol(users);
    });
  }, []);

  // close if you click outside of the menu or button
  useOnClickOutside(ref as React.RefObject<HTMLDivElement>, close);

  return (
    <StyledMenu>
      {currentUserIsOwner && (
        <>
          {/* USER SEARCH INPUT */}
          <InstantSearch indexName={`${ALGOLIA_INDEX_NAME}_items`} searchClient={searchClient}>
            <ShareSearchWrapper>
              <ShareSearchBox />
            </ShareSearchWrapper>
            <ShareUsersWrapper>
              <ShareSearchResults allUsers={allUsersLol} plate={plate} />
            </ShareUsersWrapper>
          </InstantSearch>
          {/* EDITORS */}
          <ShareUsersWrapper>
            <SharedUser addable={false} user={plate.owner} plate={plate} />
            {editors.map((user) => (
              <SharedUser addable={false} user={user} key={user.snapshot.id} plate={plate} />
            ))}
          </ShareUsersWrapper>
          {/* VISIBILITY */}
          <ShareUsersWrapper style={{ paddingBottom: "12px", userSelect: "none" }}>
            <Lock plate={plate} styles={{ position: "relative", top: "2px", left: "-3px", marginRight: "2px" }} />
            {isPublic ? "PUBLIC" : "PRIVATE"}
            <Subtext>
              {isPublic
                ? "Anyone can view this Plate"
                : editors.length
                ? "Only *you and collaborators* can view this Plate"
                : "Only *you* can view this Plate"}
            </Subtext>
          </ShareUsersWrapper>
        </>
      )}
      <SharablePageLink link={plate.externalLink} copyLink={plate.copyLink} />
    </StyledMenu>
  );
});

const StyledMenu = styled.div`
  position: absolute;
  width: ${SHARE_MENU_WIDTH}px;
  top: 45px;
  right: 30px;
  border: ${({ theme }) => `1px solid ${theme.primary}`};
  background-color: ${({ theme }) => theme.background};
  font-family: "IBM Plex Mono";
  z-index: ${Layer.WIN_LAYER};
  color: ${({ theme }) => theme.text.default};
`;

const ShareSearchWrapper = styled.div`
  max-height: 400px;
  overflow-y: auto;
  padding: 12px 12px 0 12px;
  border-bottom: ${({ theme }) => `1px solid ${theme.primary}`};
  transition: max-height 0.1s;
`;

const ShareUsersWrapper = styled.div`
  padding: 12px 15px 0 15px;
  border-bottom: ${({ theme }) => `1px solid ${theme.primary}`};
`;

const Subtext = styled.div`
  padding-top: 5px;
  font-size: 13px;
  font-weight: 400;
`;

/* -------------------------------------------------- */
/*                    Search Comps                    */
/* -------------------------------------------------- */
const ShareSearchBox = connectSearchBox(
  ({ currentRefinement, refine }: { currentRefinement: string; refine: (s: string) => void }) => {
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      refine(event.currentTarget.value);
    };

    return (
      <ShareInput
        placeholder="Add collaborators..."
        autoFocus
        style={{ height: "36px" }}
        value={currentRefinement}
        onChange={handleChange}
      />
    );
  }
);

const ShareSearchResults = connectStateResults(
  ({ searchState, allUsers, plate }: { searchState: any; allUsers: BaseUser[]; plate: Plate }) => {
    return <>{searchState && searchState.query && <CustomHits allUsers={allUsers} plate={plate} />}</>;
  }
);

const CustomHits = connectHits(
  ({ hits, allUsers, plate }: { hits: SearchHit[]; allUsers: BaseUser[]; plate: Plate }) => {
    const hitsThatAreUsers = hits.filter((hit) => hit.recordType === "user");
    const usersThatAreHits = allUsers.filter((user) =>
      hitsThatAreUsers.find((hit) => hit.username === user.snapshot.username)
    );
    const currentEditorIds = plate.observe((state) => state.editorIds);
    const withCurrentEditorsRemoved = usersThatAreHits.filter(
      (user) => !currentEditorIds.includes(user.snapshot.id)
    );
    return (
      <>
        {withCurrentEditorsRemoved.slice(0, 9).map((user) => (
          <SharedUser user={user} addable key={user.snapshot.id} plate={plate} />
        ))}
        {withCurrentEditorsRemoved.length === 0 && (
          <div style={{ paddingBottom: "12px" }}>No matching users found...</div>
        )}
      </>
    );
  }
);
